import { required } from 'react-admin';
import { Controller, useFormContext } from 'react-hook-form';
import { DEFAULT_DATE_MASK } from 'shared/mui/DatePicker/config/defaultValues';
import { DateRangeTimezonePicker } from 'shared/mui/DatePicker/DateRangeTimezonePicker/DateRangeTimezonePicker';
import { FilterFieldConfig } from 'shared/mui/Filter/config';
import { useFilterContext } from 'shared/mui/Filter/context/filterContext';
import { useFilterTimezone } from 'shared/mui/Filter/hooks/useFilterTimezone';
import { AutocompleteInput, AutocompleteMultiselectInput } from 'shared/react-admin/Inputs';
import { ReferenceArrayInput, ReferenceInput } from 'shared/react-admin/ReferenceInputs';
import { Currency } from 'types/embedded/currency';
import { Referral } from 'types/embedded/referral';

import { useExpenseSubarticlesFilter } from './useExpenseSubarticlesFilter';

export const useExpenseReportFilterFields = (): FilterFieldConfig[] => {
  const { control, resetField } = useFormContext();
  const { timezone, onChangeTimezone } = useFilterTimezone();
  const { onReset, setAppliedFilter } = useFilterContext();
  const { expenseSubarticlesFilter } = useExpenseSubarticlesFilter();

  return [
    {
      name: 'appliedDate',
      label: 'Период отчёта',
      chipType: 'dateTimeRangeTimezone',
      chipProps: {
        onDelete: onReset,
        mask: DEFAULT_DATE_MASK,
      },
      input: (
        <Controller
          control={control}
          name="appliedDate"
          render={({ field: { onChange, onBlur, value }, formState: { errors }, fieldState }) => (
            <DateRangeTimezonePicker
              error={!!errors.appliedDate}
              helperText={fieldState?.error?.message}
              label="Период отчёта"
              maxDate={new Date()}
              onBlur={onBlur}
              onChange={onChange}
              onChangeTimezone={onChangeTimezone}
              required
              size="small"
              timezone={timezone}
              value={value}
            />
          )}
          rules={{
            validate: {
              required: (value) => (!!value[0] && !!value[1]) || 'Обязательно для заполнения',
            },
          }}
        />
      ),
    },
    {
      name: 'currency',
      label: 'Валюта отчёта',
      chipType: 'text',
      chipProps: {
        onDelete: () => {
          resetField('appliedDate', { keepDirty: true });
          setAppliedFilter('appliedDate', [null, null]);

          onReset && onReset();
        },
      },
      input: (
        <ReferenceInput
          filter={{ active: true }}
          name="currency"
          perPage={1000}
          reference="currencies"
          resettable
          resource="currencies"
          sort={{ field: 'id', order: 'ASC' }}
          source="currency"
        >
          <AutocompleteInput
            clearOnBlur={false}
            filterToQuery={(searchText: string) => ({ currency: searchText })}
            helperText={false}
            label="Валюта отчёта"
            name="currency"
            optionText={(record: Currency) => `${record?.alphaCode}, ${record?.name}`}
            optionValue="alphaCode"
            validate={required()}
            variant="outlined"
          />
        </ReferenceInput>
      ),
    },
    {
      name: 'referral',
      label: 'Рефералы',
      chipType: 'multiselect',
      chipProps: {
        resource: 'referrals',
        limitTags: 1,
      },
      input: (
        <ReferenceArrayInput perPage={100} reference="referrals" source="referral">
          <AutocompleteMultiselectInput
            getOptionLabel={(record: Referral) => `${record?.refId}: ${record?.name}`}
            helperText={false}
            label="Рефералы"
            name="referral"
            size="small"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'expenseTypes',
      label: 'Типы расхода',
      chipType: 'multiselect',
      chipProps: {
        resource: 'expense_types',
        limitTags: 1,
        optionText: 'description',
      },
      input: (
        <ReferenceArrayInput perPage={100} reference="expense_types" source="expenseTypes">
          <AutocompleteMultiselectInput
            helperText={false}
            label="Типы расхода"
            name="expenseTypes"
            optionText="description"
            size="small"
            source="expenseTypes"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'expenseArticles',
      label: 'Статьи затрат',
      chipType: 'multiselect',
      chipProps: {
        resource: 'expense_articles',
        limitTags: 1,
        optionText: 'description',
      },
      input: (
        <ReferenceArrayInput perPage={100} reference="expense_articles" source="expenseArticles">
          <AutocompleteMultiselectInput
            helperText={false}
            label="Статьи затрат"
            name="expenseArticles"
            optionText="description"
            size="small"
            source="expenseArticles"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'expenseSubarticles',
      label: 'Подстатьи затрат',
      chipType: 'multiselect',
      chipProps: {
        resource: 'expense_subarticles',
        limitTags: 1,
        optionText: 'description',
      },
      input: (
        <ReferenceArrayInput
          filter={expenseSubarticlesFilter}
          perPage={100}
          reference="expense_subarticles"
          source="expenseSubarticles"
        >
          <AutocompleteMultiselectInput
            helperText={false}
            label="Подстатьи затрат"
            name="expenseSubarticles"
            optionText="description"
            size="small"
            source="expenseSubarticles"
          />
        </ReferenceArrayInput>
      ),
    },
  ];
};
